import type { ReactNode } from 'react';
import { Card, Col, ProgressBar, Row } from 'react-bootstrap';
import { KPI_NA, isKpiMissingDisplay } from '../Bias/ModuleKpiDashboard';
import '../Bias/BiasAggregateSummaryPanel.css';
import { getPDCScoreData, type PDCRun } from './pdc.utils';

const ROW_ACCENTS = ['violet', 'fuchsia', 'rose', 'emerald'] as const;

function fmtScore3(v: unknown): string {
  if (v == null || v === '') return KPI_NA;
  const n = Number(v);
  if (!Number.isFinite(n) || n < 0) return KPI_NA;
  return n.toFixed(3);
}

function fmtText(v: unknown): string {
  if (v == null || v === '') return KPI_NA;
  const s = String(v).trim();
  return s.length ? s : KPI_NA;
}

function datasetVerdictBadgeBg(raw: unknown): 'danger' | 'warning' | 'success' | 'secondary' {
  const u = String(raw ?? '')
    .trim()
    .toUpperCase();
  if (u === 'REJECT') return 'danger';
  if (u === 'REVIEW') return 'warning';
  if (u === 'APPROVE') return 'success';
  return 'secondary';
}

function riskTierHeroTone(riskTierLabel: string | null): 'high' | 'medium' | 'low' | 'unknown' {
  const r = (riskTierLabel ?? '').toUpperCase();
  if (r === 'HIGH') return 'high';
  if (r === 'MEDIUM' || r === 'MODERATE') return 'medium';
  if (r === 'LOW') return 'low';
  return 'unknown';
}

function riskTierProgressVariant(
  tone: 'high' | 'medium' | 'low' | 'unknown'
): 'danger' | 'warning' | 'success' | 'secondary' {
  if (tone === 'high') return 'danger';
  if (tone === 'medium') return 'warning';
  if (tone === 'low') return 'success';
  return 'secondary';
}

function KpiTile({ label, value, accent }: { label: string; value: ReactNode; accent: string }) {
  return (
    <div className="bias-dash-tile bias-dash-tile--compact h-100" data-accent={accent}>
      <div className="bias-dash-tile__label">{label}</div>
      <div className="bias-dash-tile__value">{value}</div>
    </div>
  );
}

export function PDCDashboard({ run }: { run: PDCRun | null | undefined }) {
  const scores = getPDCScoreData(run);

  if (!scores) {
    return (
      <div className="text-muted small py-3" aria-label="Prompt Injection KPIs">
        No Prompt Injection evaluation data available for this run.
      </div>
    );
  }

  const riskScore = fmtScore3(scores.riskScore);
  const riskScoreNum = scores.riskScore;
  const riskScoreBarNow =
    isKpiMissingDisplay(riskScore) || !Number.isFinite(riskScoreNum) || riskScoreNum < 0
      ? 0
      : Math.min(100, Math.max(0, riskScoreNum * 100));

  const tierDisplay = fmtText(scores.riskTier);
  const tierTone = riskTierHeroTone(tierDisplay === KPI_NA ? null : tierDisplay);
  const tierVariant = riskTierProgressVariant(tierTone);
  const riskScoreBarVariant =
    isKpiMissingDisplay(riskScore) || !Number.isFinite(riskScoreNum) || riskScoreNum < 0 ? 'secondary' : tierVariant;

  const datasetStatus = fmtText(scores.datasetStatus);
  const datasetVerdictRaw = fmtText(scores.verdict);
  const verdictBarVariant = datasetVerdictBadgeBg(scores.verdict);
  const verdictCardAccent =
    verdictBarVariant === 'danger'
      ? 'bias-dash-verdict-card--danger'
      : verdictBarVariant === 'warning'
      ? 'bias-dash-verdict-card--warning'
      : verdictBarVariant === 'success'
      ? 'bias-dash-verdict-card--success'
      : '';

  const row2 = [
    { label: 'PDC-01: Prompt Defense Core', value: fmtScore3(scores.detectionScore) },
    { label: 'PDC-02: PDX+ Explainability', value: fmtScore3(scores.explainabilityScore) },
    { label: 'PDC-03: ABR+ Blast Radius', value: fmtScore3(scores.businessImpactScore) },
    { label: 'PDC-04: Findings & Verdict', value: fmtScore3(scores.findingsVerdictScore) }
  ];

  return (
    <div className="bias-dash mb-2" aria-label="Prompt Defense Compliance KPIs">
      <div className="bias-dash-verdict-cards">
        <Row className="g-2 g-md-2 mt-1 align-items-stretch">
          <Col xs={12} sm={6} md={3} className="d-flex">
            <Card
              className={`bias-dash-verdict-card bias-dash-verdict-card--accent bias-dash-risk-tier-card h-100 flex-fill w-100 border-0 shadow-sm bias-dash-risk-tier-card--${tierTone}`}
            >
              <Card.Body className="p-2 p-md-3 d-flex flex-column flex-grow-1">
                <span className="bias-dash-verdict-card__title d-block mb-1">Risk Tier</span>
                <div
                  className={`bias-dash-verdict-card__value text-uppercase bias-dash-risk-tier-card__value--${tierTone}`}
                >
                  {tierDisplay}
                </div>
                <p className="bias-dash-verdict-card__hint small text-body-secondary mb-1 mb-sm-2">
                  High risk indicates low defense capability.
                </p>
                <div className="mt-auto">
                  <ProgressBar
                    now={isKpiMissingDisplay(tierDisplay) ? 0 : 100}
                    variant={tierVariant}
                    className="bias-dash-verdict-card__bar"
                  />
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col xs={12} sm={6} md={3} className="d-flex">
            <Card className="bias-dash-verdict-card bias-dash-verdict-card--accent h-100 flex-fill w-100 border-0 shadow-sm">
              <Card.Body className="p-2 p-md-3 d-flex flex-column flex-grow-1">
                <span className="bias-dash-verdict-card__title d-block mb-1">Risk Score</span>
                <div className="bias-dash-verdict-card__value">{riskScore}</div>
                <p className="bias-dash-verdict-card__hint small text-body-secondary mb-1 mb-sm-2">
                  Aggregate risk on a 0–1 scale (3&nbsp;decimal display).
                </p>
                <div className="mt-auto">
                  <ProgressBar
                    now={riskScoreBarNow}
                    variant={riskScoreBarVariant}
                    className="bias-dash-verdict-card__bar"
                  />
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col xs={12} sm={6} md={3} className="d-flex">
            <Card className="bias-dash-verdict-card bias-dash-verdict-card--accent h-100 flex-fill w-100 border-0 shadow-sm">
              <Card.Body className="p-2 p-md-3 d-flex flex-column flex-grow-1">
                <span className="bias-dash-verdict-card__title d-block mb-1">Dataset Status</span>
                <div className="bias-dash-verdict-card__value text-break">{datasetStatus}</div>
                <p className="bias-dash-verdict-card__hint small text-body-secondary mb-1 mb-sm-2">
                  Current dataset processing / readiness state.
                </p>
                <div className="mt-auto">
                  <ProgressBar
                    now={isKpiMissingDisplay(datasetStatus) ? 0 : 100}
                    variant="secondary"
                    className="bias-dash-verdict-card__bar"
                  />
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col xs={12} sm={6} md={3} className="d-flex">
            <Card
              className={`bias-dash-verdict-card bias-dash-verdict-card--accent h-100 flex-fill w-100 border-0 shadow-sm ${verdictCardAccent}`}
            >
              <Card.Body className="p-2 p-md-3 d-flex flex-column flex-grow-1">
                <span className="bias-dash-verdict-card__title d-block mb-1">Dataset Verdict</span>
                <div className="bias-dash-verdict-card__value text-uppercase">{datasetVerdictRaw}</div>
                <p className="bias-dash-verdict-card__hint small text-body-secondary mb-1 mb-sm-2">
                  Governance gate decision (REVIEW / APPROVE / REJECT).
                </p>
                <div className="mt-auto">
                  <ProgressBar
                    now={isKpiMissingDisplay(datasetVerdictRaw) ? 0 : 100}
                    variant={verdictBarVariant}
                    className="bias-dash-verdict-card__bar"
                  />
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
      <Row className="g-2 mt-2">
        {row2.map((k, i) => (
          <Col key={k.label} xs={12} sm={6} md={3}>
            <KpiTile label={k.label} value={k.value} accent={ROW_ACCENTS[i]} />
          </Col>
        ))}
      </Row>
    </div>
  );
}
